import { z } from 'zod';

export const CANNTALK_CONSENT_VERSION = 'canntalk-2026-11-v6';
export const CanNTalkModeSchema = z.enum(['live', 'demo']);
export const CanNTalkDialogueProviderSchema = z.enum(['openai', 'qwen']);
export const CanNTalkBehaviorProfileSchema = z.enum(['passive', 'active', 'supportive', 'contrarian', 'offtopic', 'interrupting']);
export type CanNTalkBehaviorProfile = z.infer<typeof CanNTalkBehaviorProfileSchema>;
export const CanNTalkProfileSchema = z.object({ id: CanNTalkBehaviorProfileSchema, labelZh: z.string(), labelEn: z.string(), descriptionZh: z.string() });
export const CanNTalkScenarioSchema = z.object({
  id: z.string(), titleZh: z.string(), titleEn: z.string(), descriptionZh: z.string(),
  category: z.enum(['academic', 'social', 'workplace']), difficulty: z.string(), durationMinutes: z.number(),
  supportedModes: z.array(z.union([z.literal(1), z.literal(2), z.literal(3)])), objectives: z.array(z.string()), vocabulary: z.array(z.string()),
});
export type CanNTalkScenario = z.infer<typeof CanNTalkScenarioSchema>;
export const CANNTALK_AVATAR_IDS = ['alan', 'chloe', 'jason', 'mei', 'leo', 'sophie', 'marcus', 'yuki', 'daniel', 'grace', 'ivan', 'natalie', 'felix', 'joyce', 'sam', 'iris', 'victor', 'tessa', 'ben', 'zoe'] as const;
export const CanNTalkAvatarIdSchema = z.enum(CANNTALK_AVATAR_IDS);
export const CanNTalkPersonaSchema = z.object({
  age: z.number().int().min(21).max(55),
  taglineZh: z.string().trim().min(1).max(80), taglineEn: z.string().trim().min(1).max(100),
  backgroundZh: z.string().trim().min(1).max(400), backgroundEn: z.string().trim().min(1).max(600),
  traitsZh: z.array(z.string().trim().min(1).max(24)).length(3),
  interestsZh: z.array(z.string().trim().min(1).max(60)).length(2),
  speakingStyleZh: z.string().trim().min(1).max(300), signaturePhraseZh: z.string().trim().min(1).max(80),
  exampleZh: z.string().trim().min(1).max(300),
  goalZh: z.string().trim().min(1).max(200), goalEn: z.string().trim().min(1).max(300),
}).strict();
export type CanNTalkPersona = z.infer<typeof CanNTalkPersonaSchema>;
export const CanNTalkVoiceSchema = z.object({
  id: z.string().min(1).max(200).regex(/^[A-Za-z0-9_.:-]+$/),
  model: z.string().min(1).max(100),
  descriptionZh: z.string().max(300), descriptionEn: z.string().max(300),
  /** Server synthesis controls captured with the session; absent on legacy voices. */
  rate: z.number().min(0.5).max(2).optional(), pitch: z.number().min(0.5).max(2).optional(),
}).strict();
export const CanNTalkAvatarSchema = z.object({
  id: z.string(), nameZh: z.string(), nameEn: z.string(), roleZh: z.string(), color: z.string(), defaultProfile: CanNTalkBehaviorProfileSchema,
  /** Optional so saved sessions from before the portrait catalogue remain readable. */
  photoUrl: z.string().max(200).optional(), voiceGender: z.enum(['male', 'female']).optional(),
  /** Fictional background and speech habits captured with the session, never backfilled into older sessions. */
  roleEn: z.string().trim().min(1).max(100).optional(), persona: CanNTalkPersonaSchema.optional(),
  /** A stable provider voice identity, captured with each new session. Contains no API credentials. */
  voice: CanNTalkVoiceSchema.optional(),
});
export type CanNTalkAvatar = z.infer<typeof CanNTalkAvatarSchema>;
export const CanNTalkSessionAvatarSchema = CanNTalkAvatarSchema.extend({ profile: CanNTalkBehaviorProfileSchema });
export const CanNTalkTurnSchema = z.object({
  id: z.string().uuid(), role: z.enum(['learner', 'avatar']), speakerId: z.string(), speakerNameZh: z.string(), text: z.string(), createdAt: z.string().datetime(),
  clientRequestId: z.string().uuid().optional(),
  /** Recognised partial speech cut short by a configured interrupting partner. */
  interruptedByAvatarId: CanNTalkAvatarIdSchema.optional(),
});
export type CanNTalkTurn = z.infer<typeof CanNTalkTurnSchema>;
export const CanNTalkEvidenceSchema = z.object({ turnId: z.string().uuid(), quote: z.string() });
export const CanNTalkFeedbackSchema = z.object({
  titleZh: z.string(), textZh: z.string(), dimension: z.enum(['interaction', 'vocabulary', 'structure']), evidence: z.array(CanNTalkEvidenceSchema),
});
export type CanNTalkFeedback = z.infer<typeof CanNTalkFeedbackSchema>;
export const CanNTalkReportSchema = z.object({
  mode: CanNTalkModeSchema, summaryZh: z.string(), limitationZh: z.string(), learnerTurnCount: z.number().int(), totalTurns: z.number().int(),
  dimensions: z.array(z.object({ id: z.string(), titleZh: z.string(), status: z.enum(['observed', 'not_assessed', 'needs_evidence']), feedbackZh: z.string(), evidence: z.array(CanNTalkEvidenceSchema) })),
  fillerCounts: z.array(z.object({ phrase: z.string(), count: z.number().int().min(0) })),
  rewrites: z.array(z.object({ turnId: z.string().uuid(), original: z.string(), suggestedZh: z.string(), reasonZh: z.string() })),
  resources: z.array(z.object({ id: z.string(), titleZh: z.string(), titleEn: z.string(), href: z.string(), learningNeedZh: z.string(), returnScenarioId: z.string() })),
});
export type CanNTalkReport = z.infer<typeof CanNTalkReportSchema>;
export const CanNTalkSessionSchema = z.object({
  id: z.string().uuid(), scenarioId: z.string(), avatarCount: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  /** Historical unowned demonstrations remain administrator-only; never infer an owner. */
  ownerId: z.string().min(1).max(200).optional(),
  ownerKind: z.enum(['account', 'reviewer', 'bootstrap']).optional(),
  /** Published content copied at creation; optional for sessions saved before scenario authoring. */
  scenarioSnapshot: CanNTalkScenarioSchema.optional(),
  profiles: z.record(CanNTalkBehaviorProfileSchema), avatars: z.array(CanNTalkSessionAvatarSchema), turns: z.array(CanNTalkTurnSchema),
  startedAt: z.string().datetime(), completedAt: z.string().datetime().optional(), report: CanNTalkReportSchema.optional(),
  formativeFeedback: CanNTalkFeedbackSchema.optional(), mode: CanNTalkModeSchema,
  provider: CanNTalkDialogueProviderSchema.nullable().optional(), model: z.string().nullable().optional(),
  consent: z.object({ version: z.string(), accepted: z.literal(true), acceptedAt: z.string().datetime() }),
});
export type CanNTalkSession = z.infer<typeof CanNTalkSessionSchema>;
export const CanNTalkBootstrapSchema = z.object({
  role: z.enum(['learner', 'teacher', 'admin']),
  mode: CanNTalkModeSchema,
  dialogue: z.object({ provider: CanNTalkDialogueProviderSchema.nullable(), model: z.string().nullable(), available: z.boolean() }),
  speech: z.object({ available: z.boolean(), provider: z.literal('qwen').nullable(), model: z.string().nullable(), voiceMode: z.enum(['custom', 'official-cantonese']).nullable().optional() }).optional(),
  demoNoticeZh: z.string(), consentVersion: z.string(), scenarios: z.array(CanNTalkScenarioSchema),
  avatars: z.array(CanNTalkAvatarSchema), profiles: z.array(CanNTalkProfileSchema), sessions: z.array(CanNTalkSessionSchema),
  progress: z.object({ completedSessions: z.number().int(), learnerTurns: z.number().int(), practicedScenarioIds: z.array(z.string()), lastPracticeAt: z.string().nullable() }),
});
export type CanNTalkBootstrap = z.infer<typeof CanNTalkBootstrapSchema>;
export const CanNTalkCreateSessionSchema = z.object({
  scenarioId: z.string().min(1), avatarCount: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  avatarIds: z.array(CanNTalkAvatarIdSchema).min(1).max(3).optional(),
  mode: CanNTalkModeSchema.optional().default('demo'),
  profiles: z.record(CanNTalkBehaviorProfileSchema).default({}),
  consent: z.object({ version: z.literal(CANNTALK_CONSENT_VERSION), accepted: z.literal(true) }),
}).strict().superRefine((body, context) => {
  if (!body.avatarIds) return;
  if (body.avatarIds.length !== body.avatarCount) context.addIssue({ code: z.ZodIssueCode.custom, path: ['avatarIds'], message: 'Choose exactly avatarCount avatars.' });
  if (new Set(body.avatarIds).size !== body.avatarIds.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ['avatarIds'], message: 'Choose each avatar only once.' });
});
export const CanNTalkCreateTurnSchema = z.object({
  text: z.string().trim().min(1).max(1200), requestId: z.string().uuid().optional(),
  expectedTurnCount: z.number().int().min(0).max(1000).optional(),
  interruptedByAvatarId: CanNTalkAvatarIdSchema.optional(),
}).strict();
